import type { CSSProperties, FC, ReactNode } from 'react'

import Link from 'next/link'
import { useRef } from 'react'
import { ArrowRight, Check } from 'lucide-react'
import { cn } from '@/lib/utils'
import { gsap, useGSAP } from '@/lib/gsap'
import { Eyebrow } from '@/components/generals/eyebrow'
import { Container } from '@/components/generals/container'
import { RevealText } from '@/components/generals/reveal-text'
import { FeaturePreview } from '@/components/features/feature-preview'
import { FEATURES } from '@/constants/features'

const STACK_QUERY = '(min-width: 1024px) and (min-height: 760px) and (prefers-reduced-motion: no-preference)'

/**
 * Homepage features: an editorial index followed by six full-height
 * panels that alternate burgundy and cream. On large screens each panel
 * stays pinned while the next one slides over it.
 */
export const FeaturesStack: FC = (): ReactNode => {
    const stackRef = useRef<HTMLDivElement>(null)

    useGSAP(() => {
        const stack = stackRef.current

        if (!stack) return

        const media = gsap.matchMedia()

        media.add(STACK_QUERY, () => {
            const panels = Array.from(stack.querySelectorAll<HTMLElement>('[data-feature-panel]'))

            // Sticky panels report their stuck position, so measure from the
            // non-sticky stack container using the panels' heights instead.
            const offsetOf = (count: number): number => {
                return panels.slice(0, count).reduce((sum, panel) => sum + panel.offsetHeight, 0)
            }

            panels.forEach((panel, index) => {
                if (index === panels.length - 1) return

                const inner = panel.querySelector<HTMLElement>('[data-feature-inner]')
                const shade = panel.querySelector<HTMLElement>('[data-feature-shade]')

                if (!inner || !shade) return

                gsap.timeline({
                    scrollTrigger: {
                        trigger: stack,
                        start: () => `top+=${offsetOf(index + 1)} bottom`,
                        end: () => `top+=${offsetOf(index + 1)} top`,
                        scrub: true,
                        invalidateOnRefresh: true
                    }
                })
                    .to(inner, { scale: 0.93, y: -40, borderRadius: 32, ease: 'none' }, 0)
                    .to(shade, { opacity: 0.45, ease: 'none' }, 0)
            })
        })

        return () => media.revert()
    }, { scope: stackRef })

    return (
        <section id='features' aria-labelledby='features-title'>
            <div className='bg-cream'>
                <Container className='py-28 md:py-40'>
                    <div className='grid gap-14 lg:grid-cols-12 lg:gap-10'>
                        <div className='lg:col-span-6'>
                            <Eyebrow>Features</Eyebrow>

                            <RevealText
                                as='h2'
                                id='features-title'
                                text='Everything you need to understand your finances.'
                                highlight={['understand']}
                                highlightClassName='text-stone'
                                className='mt-6 font-heading text-display-xl font-medium text-burgundy'
                            />
                        </div>

                        <div className='lg:col-span-5 lg:col-start-8'>
                            <p data-reveal='' className='text-lg leading-relaxed text-stone-dark'>
                                Six capabilities, one connected system. Every report, forecast and insight is built from the same reconciled data, so your numbers always agree.
                            </p>

                            <ol className='mt-10 border-t border-burgundy/10'>
                                {FEATURES.map((feature, index) => (
                                    <li
                                        key={feature.id}
                                        data-reveal=''
                                        className='border-b border-burgundy/10'
                                        style={{ '--reveal-delay': `${index * 60}ms` } as CSSProperties}
                                    >
                                        <a
                                            href={`#feature-${feature.id}`}
                                            className='group flex items-center gap-5 py-4 text-burgundy transition-colors duration-300 hover:text-burgundy-muted'
                                        >
                                            <span className='font-mono text-xs text-stone'>{feature.number}</span>
                                            <span className='text-[15px] font-medium'>{feature.title}</span>
                                            <ArrowRight aria-hidden='true' className='ml-auto size-4 -translate-x-1 opacity-0 transition-all duration-300 group-hover:translate-x-0 group-hover:opacity-100' />
                                        </a>
                                    </li>
                                ))}
                            </ol>
                        </div>
                    </div>
                </Container>
            </div>

            <div ref={stackRef}>
                {FEATURES.map((feature, index) => {
                    const dark = index % 2 === 0
                    const previewFirst = index % 2 === 1

                    return (
                        <article
                            key={feature.id}
                            id={`feature-${feature.id}`}
                            data-feature-panel=''
                            aria-labelledby={`feature-${feature.id}-title`}
                            className={cn(
                                'relative scroll-mt-0 [@media(min-width:1024px)_and_(min-height:760px)]:sticky [@media(min-width:1024px)_and_(min-height:760px)]:top-0',
                                dark ? 'bg-cream' : 'bg-burgundy'
                            )}
                        >
                            <div
                                data-feature-inner=''
                                className={cn(
                                    'relative flex min-h-[100svh] items-center overflow-hidden will-change-transform',
                                    dark ? 'bg-burgundy text-cream' : 'bg-cream text-burgundy'
                                )}
                            >
                                <div data-feature-shade='' aria-hidden='true' className='pointer-events-none absolute inset-0 z-30 bg-burgundy-deep opacity-0' />

                                <Container className='grid gap-14 py-24 lg:grid-cols-12 lg:items-center lg:gap-10 lg:py-20'>
                                    <div className={cn('lg:col-span-5', previewFirst && 'lg:order-2 lg:col-start-8')}>
                                        <p className={cn('flex items-center gap-4 font-mono text-[11px] font-medium uppercase tracking-[0.2em]', dark ? 'text-beige' : 'text-stone-dark')}>
                                            <span>{feature.number}</span>
                                            <span aria-hidden='true' className={cn('h-px w-10', dark ? 'bg-cream/25' : 'bg-burgundy/25')} />
                                            <span>{feature.title}</span>
                                        </p>

                                        <h3
                                            id={`feature-${feature.id}-title`}
                                            className='mt-7 font-heading text-display-lg font-medium'
                                        >
                                            {feature.headline}
                                        </h3>

                                        <p className={cn('mt-6 max-w-md text-lg leading-relaxed', dark ? 'text-cream/70' : 'text-stone-dark')}>
                                            {feature.summary}
                                        </p>

                                        <ul className='mt-8 space-y-3'>
                                            {feature.points.map(point => (
                                                <li key={point} className={cn('flex items-start gap-3 text-[15px]', dark ? 'text-cream/85' : 'text-burgundy')}>
                                                    <span className={cn('mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full', dark ? 'bg-cream/10 text-gold' : 'bg-burgundy/[0.07] text-gold-dark')}>
                                                        <Check aria-hidden='true' className='size-3' />
                                                    </span>
                                                    {point}
                                                </li>
                                            ))}
                                        </ul>

                                        <Link
                                            href={`/features#${feature.id}`}
                                            className={cn(
                                                'group mt-10 inline-flex items-center gap-2 border-b pb-1 text-sm font-medium transition-colors duration-300',
                                                dark ? 'border-cream/30 text-cream hover:border-cream' : 'border-burgundy/30 text-burgundy hover:border-burgundy'
                                            )}
                                        >
                                            Explore {feature.title.toLowerCase()}
                                            <ArrowRight aria-hidden='true' className='size-4 transition-transform duration-300 group-hover:translate-x-0.5' />
                                        </Link>
                                    </div>

                                    <div
                                        data-reveal='scale'
                                        className={cn('lg:col-span-7', previewFirst ? 'lg:order-1 lg:col-start-1' : 'lg:col-start-6 lg:pl-6')}
                                    >
                                        <div className='mx-auto max-w-[640px] sm:px-6 lg:px-0'>
                                            <FeaturePreview feature={feature.id} />
                                        </div>
                                    </div>
                                </Container>
                            </div>
                        </article>
                    )
                })}
            </div>
        </section>
    )
}
